import {
  buildStoredFromLocal,
  deriveLocalState,
  derivePlacementSummary,
  LocalState,
  Placement,
  PLACEMENTS,
  Side,
  SIDES,
  sideToKey,
  StripSlotKey,
} from '@cutr/constants/windows/strip-placement.ts';
import { ConfigurationValues } from '@cutr/constants/windows/windows';
import {
  BasicValue,
  CONFIGURATIONS_OPTIONS,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useId, useMemo, useState } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { Checkbox } from '@/primitives/Checkbox';
import { Label } from '@/windows/primitives/Label';
import { Radio } from '@/windows/primitives/Radio';
import { RadioGroup } from '@/windows/primitives/RadioGroup';

import { SubSubheader, unboxLabel } from '../Configurations';
import { useConfigurations } from '../ConfigurationsContext';
import { LocalGridSelectModal } from '../inputs/LocalGridSelectModal';
import { ConfigurationComponent } from '.';
import styles from './PlasteringStripsConfig.module.css';

type PlasteringStripsValue = NonNullable<
  ConfigurationValues['plasteringStrips']
>;

const PLASTERING_STRIPS_ALL_OPTIONS =
  CONFIGURATIONS_OPTIONS.plasteringStrips.insideTop.options;

const NONE_OPTION = PLASTERING_STRIPS_ALL_OPTIONS.find(
  (o) => o.value === 'none'
)!;

const PlasteringStripsInput = () => {
  const { t } = useTranslation();
  const placementLabelId = useId();
  const placementInputId = useId();
  const locationsLabelId = useId();
  const { configurations, dispatch } = useConfigurations();

  const [initial] = useState(() =>
    deriveLocalState(configurations.plasteringStrips, NONE_OPTION)
  );
  const [placement, setPlacement] = useState<Placement>(initial.placement);
  const [locations, setLocations] = useState<Set<Side>>(initial.locations);
  const [profile, setProfile] = useState<BasicValue>(initial.profile);
  const [customSlots, setCustomSlots] = useState<PlasteringStripsValue>(
    initial.customSlots
  );

  const profileOptions = useMemo(
    () =>
      PLASTERING_STRIPS_ALL_OPTIONS.map((config) => ({
        config,
        imagePath:
          config.value === 'none'
            ? '/sorpetaler/none.jpg'
            : `/sorpetaler/plasteringStrips/${config.value}.png`,
      })),
    []
  );

  const dispatchFromLocal = (next: LocalState) => {
    dispatch({
      type: 'SET_PLASTERING_STRIPS',
      value: buildStoredFromLocal(next, NONE_OPTION),
    });
  };

  const handlePlacementChange = (next: Placement) => {
    const nextCustomSlots =
      next === 'custom' && placement !== 'custom'
        ? buildStoredFromLocal(
            { placement, locations, profile, customSlots },
            NONE_OPTION
          )
        : customSlots;
    setPlacement(next);
    setCustomSlots(nextCustomSlots);
    dispatchFromLocal({
      placement: next,
      locations,
      profile,
      customSlots: nextCustomSlots,
    });
  };

  const handleLocationToggle = (side: Side, checked: boolean) => {
    const next = new Set(locations);
    if (checked) next.add(side);
    else next.delete(side);
    setLocations(next);
    dispatchFromLocal({ placement, locations: next, profile, customSlots });
  };

  const handleProfileChange = (next: BasicValue) => {
    setProfile(next);
    dispatchFromLocal({ placement, locations, profile: next, customSlots });
  };

  const handleCustomSlotChange = (
    key: keyof PlasteringStripsValue,
    value: BasicValue
  ) => {
    const next = { ...customSlots, [key]: value };
    setCustomSlots(next);
    dispatchFromLocal({ placement, locations, profile, customSlots: next });
  };

  const showSharedInputs =
    placement === 'interior' ||
    placement === 'exterior' ||
    placement === 'both';

  return (
    <>
      <Label id={placementLabelId} htmlFor={placementInputId}>
        {t('windows.configurations.plasteringStrips.placement.label')}
      </Label>
      <RadioGroup
        aria-labelledby={placementLabelId}
        id={placementInputId}
        orientation="horizontal"
        value={placement}
        style={{ minHeight: '2rem' }}
        onChange={(value) => handlePlacementChange(value as Placement)}
      >
        {PLACEMENTS.map((option) => (
          <Radio key={option} value={option}>
            {t(
              `windows.configurations.plasteringStrips.placement.options.${option}`
            )}
          </Radio>
        ))}
      </RadioGroup>

      {showSharedInputs && (
        <>
          <Label id={locationsLabelId}>
            {t('windows.configurations.plasteringStrips.locationOnFrame.label')}
          </Label>
          <div
            role="group"
            aria-labelledby={locationsLabelId}
            className={styles.locationsGroup}
          >
            {SIDES.map((side) => (
              <Checkbox
                key={side}
                checked={locations.has(side)}
                onChange={(e) => handleLocationToggle(side, e.target.checked)}
              >
                {t(
                  `windows.configurations.plasteringStrips.locationOnFrame.options.${side}`
                )}
              </Checkbox>
            ))}
          </div>
          <Label>
            {t('windows.configurations.plasteringStrips.profile.label')}
          </Label>
          <LocalGridSelectModal
            modalTitle={t(
              'windows.configurations.plasteringStrips.profile.modal.title'
            )}
            modalDescription={t(
              'windows.configurations.plasteringStrips.profile.modal.description'
            )}
            options={profileOptions}
            value={profile}
            onChange={handleProfileChange}
          />
        </>
      )}

      {placement === 'custom' &&
        (['inside', 'outside'] as const).map((face) => (
          <Fragment key={face}>
            <SubSubheader
              title={t(
                `windows.configurations.plasteringStrips.custom.${face}.label`
              )}
            />
            {SIDES.map((side) => {
              const key = sideToKey(face, side);
              return (
                <Fragment key={key}>
                  <Label>
                    {t(
                      `windows.configurations.plasteringStrips.custom.${face}.${side}`
                    )}
                  </Label>
                  <LocalGridSelectModal
                    modalTitle={t(
                      `windows.configurations.plasteringStrips.custom.${face}.${side}`
                    )}
                    modalDescription={t(
                      'windows.configurations.plasteringStrips.profile.modal.description'
                    )}
                    options={profileOptions}
                    value={customSlots[key]}
                    onChange={(value) => handleCustomSlotChange(key, value)}
                  />
                </Fragment>
              );
            })}
          </Fragment>
        ))}
    </>
  );
};

const CUSTOM_SLOT_LOCATION: Record<
  StripSlotKey,
  { face: 'inside' | 'outside'; side: Side }
> = {
  insideTop: { face: 'inside', side: 'top' },
  insideRight: { face: 'inside', side: 'right' },
  insideBottom: { face: 'inside', side: 'bottom' },
  insideLeft: { face: 'inside', side: 'left' },
  outsideTop: { face: 'outside', side: 'top' },
  outsideRight: { face: 'outside', side: 'right' },
  outsideBottom: { face: 'outside', side: 'bottom' },
  outsideLeft: { face: 'outside', side: 'left' },
};

const config: ConfigurationComponent<'plasteringStrips'> = {
  key: 'plasteringStrips',
  input: <PlasteringStripsInput />,
  display: (value: ConfigurationValues['plasteringStrips']) => {
    const summary = derivePlacementSummary(value);
    if (!summary) return '-';

    if (summary.placement === 'custom') {
      return (
        <div className={styles.display}>
          {summary.customEntries.map(([key, v]) => {
            const { face, side } = CUSTOM_SLOT_LOCATION[key];
            const faceLabel = i18n.t(
              `windows.configurations.plasteringStrips.custom.${face}.label`
            );
            const sideLabel = i18n.t(
              `windows.configurations.plasteringStrips.custom.${face}.${side}`
            );
            return (
              <div key={key}>
                <span>
                  {faceLabel} — {sideLabel}:{' '}
                </span>
                <span>{unboxLabel(v.label)}</span>
              </div>
            );
          })}
        </div>
      );
    }

    const placementLabel = i18n.t(
      `windows.configurations.plasteringStrips.placement.options.${summary.placement}`
    );
    const sidesLabel = summary.sides
      .map((side) =>
        i18n.t(
          `windows.configurations.plasteringStrips.locationOnFrame.options.${side}`
        )
      )
      .join(', ');

    return (
      <div className={styles.display}>
        <div>
          <span>
            {placementLabel}, {sidesLabel}:{' '}
          </span>
          <span>{unboxLabel(summary.profile.label)}</span>
        </div>
      </div>
    );
  },
};

export default config;
